import { NotificationStatusEnum } from '@elearning-platform/api-types/enums';
import { Breakpoint, Color, IconName, Loader, SimpleMarkdown, useFormatDate, UseFormatDate } from '@elearning-platform/ui';
import { useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import React, { MouseEvent } from 'react';
import { useCollapse } from 'react-collapsed';
import { TransProps, useTranslation } from 'react-i18next';

import { ButtonVariant } from '../../../../components/button/button.constants';
import { Tooltip } from '../../../../components/tooltip/tooltip.component';

import {
  Container,
  Content,
  ContentInner,
  Date,
  DateInfo,
  DeleteButton,
  Dot,
  Header,
  IconContainer,
  IconWrapper,
  LoaderContainer,
  StyledIcon,
  Text,
  Title,
  TitleContainer,
  TitleWrapper,
  TrashButton,
  TrashIcon,
} from './notification.styled';
import { NotificationProps } from './notification.types';
import { convertLinkTags, getIcon, stripIndentation } from './notification.utils';

export const Notification: React.FC<NotificationProps> = (props: NotificationProps): JSX.Element => {
  const { content, onDelete, onRead, isRemoving }: NotificationProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { formatDate, getTimeFromNowHumanized, getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const isTablet: boolean = useMediaQuery(Breakpoint.Tablet);
  const { getCollapseProps, getToggleProps, isExpanded }: ReturnType<typeof useCollapse> = useCollapse();
  const isUnread: boolean = content.status.name === NotificationStatusEnum.RECEIVED;
  const handleToggle: () => void = (): void => {
    if (!isExpanded && isUnread) {
      onRead();
    }
  };

  const handleDeleteAction: (e: MouseEvent<HTMLButtonElement>) => void = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onDelete();
  };

  const createdAt: number = getTimestampMilisecValue(content.timestamp);
  const displayDate: string = !dayjs(createdAt).isSame(dayjs(), 'day')
    ? formatDate(createdAt, 'DD MMMM YYYY')
    : dayjs().diff(createdAt, 'minute') >= 60
      ? formatDate(createdAt, 'HH:mm')
      : getTimeFromNowHumanized(createdAt, true);

  return (
    <Container>
      <Header {...getToggleProps({ onClick: handleToggle })}>
        <TitleContainer>
          <IconWrapper>
            <IconContainer $isAward={!!content.type.name.startsWith('award')}>
              {getIcon(content.type.name)}
            </IconContainer>
          </IconWrapper>
          <TitleWrapper>
            <Title>{content.title}</Title>
            <DateInfo>
              {!!isUnread && <Dot />}
              <Date $isUnread={isUnread}>
                {displayDate}
              </Date>
            </DateInfo>
          </TitleWrapper>
        </TitleContainer>
        <StyledIcon $isRotated={!!isExpanded} color={Color.Gray500} name={IconName.ChevronUp} />
      </Header>
      <Content {...getCollapseProps()}>
        <ContentInner>
          <Text>
            <SimpleMarkdown text={stripIndentation(convertLinkTags(content.message))} />
          </Text>
          {isRemoving
            ? (
                <LoaderContainer>
                  <Loader size={16} />
                </LoaderContainer>
              )
            : isTablet
              ? (
                  <Tooltip title={t('competence.app.global.delete')}>
                    <TrashButton onClick={handleDeleteAction}>
                      <TrashIcon />
                    </TrashButton>
                  </Tooltip>
                )
              : (
                  <DeleteButton onClick={handleDeleteAction} variant={ButtonVariant.Outlined}>
                    {t('competence.app.global.delete')}
                  </DeleteButton>
                )
          }
        </ContentInner>
      </Content>
    </Container>
  );
};
